import {z} from "zod/v4-mini"

const isArrayBufferLike = (x: unknown): x is ArrayBufferLike => typeof x === "object" && (x instanceof ArrayBuffer || x instanceof SharedArrayBuffer)
const arrayBufferLike = z.custom<ArrayBufferLike>(isArrayBufferLike, {message: "Expected ArrayBuffer or SharedArrayBuffer"})

export const typedImageData = z.object({
    data: z.object({
        buffer: arrayBufferLike,
        byteLength: z.number().check(z.nonnegative()),
        byteOffset: z.number().check(z.nonnegative()),
    }),
    width: z.number().check(z.int(), z.positive()),
    height: z.number().check(z.int(), z.positive()),
    channelLayout: z.enum(["RGB", "BGR", "ARGB", "RGBA", "ABGR", "BGRA", "L"]),
    dataType: z.enum(["uint8", "uint16", "float16", "float32"]),
    colorSpace: z.enum(["sRGB", "linear"]),
    dpi: z.optional(z.number().check(z.positive())),
})

export type MultichannelLayouts = "RGB" | "BGR" | "ARGB" | "RGBA" | "ABGR" | "BGRA"
export interface TypedImageData<DataT = ArrayBufferView, ChannelLayouts = "L" | MultichannelLayouts, DataTypes = "uint8" | "uint16" | "float16" | "float32"> {
    readonly data: DataT
    readonly width: number
    readonly height: number
    readonly channelLayout: ChannelLayouts
    readonly dataType: DataTypes
    readonly colorSpace: "sRGB" | "linear"
    readonly dpi?: number
    //TODO: add a premultipliedAlpha field
}

export const isTypedImageData = (x: unknown): x is TypedImageData => {
    return (
        "data" in (x as any) &&
        "width" in (x as any) &&
        "height" in (x as any) &&
        "channelLayout" in (x as any) &&
        "dataType" in (x as any) &&
        "colorSpace" in (x as any)
    )
}

export function getNumChannels(imageOrChannelLayout: Pick<TypedImageData, "channelLayout"> | TypedImageData["channelLayout"]): number {
    const channelLayout = typeof imageOrChannelLayout === "object" ? imageOrChannelLayout.channelLayout : imageOrChannelLayout // TODO how to better distinguish those types ?
    switch (channelLayout) {
        case "L":
            return 1
        case "RGB":
            return 3
        case "BGR":
            return 3
        case "ARGB":
            return 4
        case "RGBA":
            return 4
        case "ABGR":
            return 4
        case "BGRA":
            return 4
    }
}

export function getBytesPerChannel(imageOrDataType: Pick<TypedImageData, "dataType"> | TypedImageData["dataType"]): number {
    const dataType = typeof imageOrDataType === "object" ? imageOrDataType.dataType : imageOrDataType // TODO how to better distinguish those types ?
    switch (dataType) {
        case "float32":
            return 4
        case "float16":
            return 2
        case "uint16":
            return 2
        case "uint8":
            return 1
    }
}

export function getBytesPerPixel(image: Pick<TypedImageData, "dataType" | "channelLayout">): number {
    return getNumChannels(image) * getBytesPerChannel(image)
}

export function getTypedArrayConstructorByDataType(
    dataType: TypedImageData["dataType"],
): Uint8ArrayConstructor | Uint16ArrayConstructor | Float32ArrayConstructor {
    switch (dataType) {
        case "uint8":
            return Uint8Array
        case "uint16":
            return Uint16Array
        case "float16":
            return Uint16Array
        case "float32":
            return Float32Array
        default:
            throw new Error(`Invalid data type: ${dataType}`)
    }
}

export function createImageData(
    width: number,
    height: number,
    channelLayout: TypedImageData["channelLayout"],
    dataType: TypedImageData["dataType"],
    colorSpace: "sRGB" | "linear",
    dpi?: number,
): TypedImageData {
    return {
        data: new (getTypedArrayConstructorByDataType(dataType))(width * height * getNumChannels(channelLayout)),
        width,
        height,
        channelLayout,
        dataType,
        colorSpace,
        dpi,
    }
}
